import { useEffect, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router';

import { isCryptoAvailable } from '@/crypto';
import { AppCard, FlowGuide } from '@/components/AppCard';
import { HomeTiles } from '@/components/HomeTiles';
import { HowLink } from '@/components/HowBridgeWorks';
import { ArrowLeftIcon, ArrowRightIcon } from '@/components/Icons';
import { IntroPanel } from '@/components/IntroPanel';
import { LinkOptions } from '@/components/LinkOptions';
import { Page } from '@/components/Layout';
import { SecretForm } from '@/components/SecretForm';
import { SharePending } from '@/components/SharePending';
import { useCreateBridge } from '@/hooks/useCreateBridge';
import { useSecretDraft } from '@/hooks/useSecretDraft';
import { type Kind } from '@/services/createBridge';
import { flowOf, kindOf } from '@/services/homeKinds';
import { t, tr, useLocale, type MessageKey } from '@/i18n';

import styles from '@/components/Home.module.css';
import shared from './pages.module.css';

/** The panel's name; on a phone the one-time secret's breaks after its comma. */
function titleOf(kind: Kind): React.ReactNode {
  return kind === 'password' ? tr('homePage.titlePasswordBreak', { br: () => <br className={shared.phoneBreak} /> }) : t(TITLES[kind]);
}

const TITLES: Record<Kind, MessageKey> = {
  password: 'homePage.titlePassword',
  chat: 'homePage.titleChat',
  receive: 'homePage.titleReceive',
};

/**
 * The app window. No landing, no hero: first a choice of what you are here
 * for (password, file, secure chat, receive), then one focused panel for it.
 * Options stay out of the way until they are needed.
 */
export function HomePage(): React.JSX.Element {
  const { code: language } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = kindOf(searchParams.get('do'));
  const [ttl, setTtl] = useState<number>(86_400);
  const [views, setViews] = useState<number>(1);
  const [lastKind, setLastKind] = useState<Kind | null>(kind);
  const [dragging, setDragging] = useState(false);
  const cryptoOk = isCryptoAvailable();
  const draft = useSecretDraft(kind === 'password');
  const { stage, createStep, uploadProgress, cancelling, error, setError, create, cancelUpload } = useCreateBridge(
    kind,
    { secretText: draft.secretText, file: draft.file, ttl, views },
    draft.validationError === null && draft.hasContent,
  );
  const { file, setFile, textarea } = draft;

  useEffect(() => {
    document.title = kind ? t('homePage.docTitle', { title: t(TITLES[kind]) }) : t('homePage.docTitleHome');
  }, [kind, language]);

  useEffect(() => {
    if (kind === 'password' && stage === 'input') textarea.current?.focus({ preventScroll: true });
  }, [kind, stage, textarea]);

  const choose = (next: Kind | null): void => {
    setError(null);
    setLastKind(kind);
    setSearchParams(next ? { do: next } : {}, { replace: next === null });
  };

  const dragProps = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => {
      setDragging(false);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setDragging(false);
      const dropped = event.dataTransfer.files[0];
      if (dropped) {
        setFile(dropped);
        if (kind !== 'password') choose('password');
      }
    },
  };

  const canCreate = cryptoOk && draft.validationError === null && draft.hasContent;

  // How long the bridge lives and how many may open it: one quiet pill beside the button.
  const options =
    kind === null || kind === 'receive' ? null : (
      <LinkOptions ttl={ttl} onTtl={setTtl} views={views} onViews={setViews} counts={kind === 'chat' ? 'devices' : 'views'} />
    );

  // a file on its way stays in the field, its plate filling left to right; the share screen follows once it is up
  const uploadingFile = stage === 'creating' && kind === 'password' && file !== null;

  let panel: React.JSX.Element;
  if (stage === 'creating' && !uploadingFile) {
    // The screen the bridge is heading for, at once; its progress ticks in the guide.
    panel = <SharePending waiting={kind === 'chat' || kind === 'receive'} purpose={kind === 'receive' ? 'receive' : 'chat'} />;
  } else if (kind === null) {
    panel = <HomeTiles cryptoOk={cryptoOk} dragging={dragging} dragProps={dragProps} onChoose={choose} />;
  } else {
    const head = (
      <div className={shared.panelHead}>
        <button type="button" className={shared.backBtn} onClick={() => { choose(null); }} aria-label={t('homePage.back')} data-testid="back">
          <ArrowLeftIcon size={18} />
        </button>
        <h2 className={shared.panelTitle}>{titleOf(kind)}</h2>
      </div>
    );
    panel =
      kind === 'password' ? (
        <SecretForm
          draft={draft}
          head={head}
          dragging={dragging}
          dragProps={dragProps}
          uploading={uploadingFile}
          uploadProgress={uploadProgress}
          cancelling={cancelling}
          error={error}
          canCreate={canCreate}
          options={options}
          onSubmit={() => void create()}
          onCancelUpload={cancelUpload}
        />
      ) : (
        <IntroPanel kind={kind} head={head} error={error} canCreate={canCreate} options={options} onCreate={() => void create()} />
      );
  }

  // the upload shows on the file's plate only: a line under the guide's step would push the steps below it down
  const progress = kind === null ? null : stage === 'input' ? 0 : createStep;

  return (
    <Page compact>
      <AppCard
        guide={
          <FlowGuide
            flow={flowOf(kind)}
            progress={progress}
            optional={kind === null}
            footer={
              kind === null ? (
                <HowLink className={styles.guideMore} testId="how-more">
                  <span className={styles.guideMoreTitle}>{t('homePage.whySafe')}</span>
                  <span className={styles.guideMoreText}>{t('homePage.howTravels')}</span>
                  <span className={styles.guideMoreArrow} aria-hidden="true">
                    <ArrowRightIcon size={16} />
                  </span>
                </HowLink>
              ) : null
            }
          />
        }
        stepKey={`${kind ?? 'tiles'}-${uploadingFile ? 'input' : stage}`}
        direction={kind === null && lastKind !== null ? 'back' : 'forward'}
        busy={stage === 'creating'}
        testId="home-card"
      >
        {panel}
      </AppCard>
    </Page>
  );
}
